/* eslint-disable react/no-unstable-nested-components */
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Box,
  Chip,
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
  CardContent,
  Autocomplete,
  ToggleButton,
  InputAdornment,
  ToggleButtonGroup,
} from '@mui/material';

import { paths } from 'src/routes/routes/paths';

import { getAllRuleTemplateList } from 'src/server/store/automation';

import Iconify from 'src/components/iconify';

import { AUTOMATION_SORT } from './AutomationSort';

// ----------------------------------------------------------------------

export const redirectLinks = (
  <Stack
    sx={{ position: 'absolute', top: -1, right: '-10px', zIndex: 1 }}
    flexDirection="row"
    alignItems="center"
    justifyContent="flex-end"
    spacing={0}
  >
    {' '}
    <Link to={`${paths.dashboard.automation}?sort=AUTOMATION`}>
      <Chip
        size="small"
        sx={{
          borderRight: 'none',
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
          borderBottomRightRadius: 0,
          px: 1,
          borderTopColor: '#919eab1f',
          fontSize: '12px',
          minWidth: '100px',
        }}
        label="Automation"
        variant="outlined"
      />
    </Link>
    <Link to={`${paths.dashboard.automation}?sort=FINDINGS`}>
      <Chip
        size="small"
        sx={{
          marginLeft: '-5px',
          borderRight: 'none',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          px: 1,
          borderTopColor: '#919eab1f',
          fontSize: '12px',
          minWidth: '100px',
        }}
        label="Finding"
        variant="outlined"
      />
    </Link>
    <Link to={`${paths.dashboard.automation}?sort=RULE`}>
      <Chip
        size="small"
        sx={{
          marginLeft: '-8px',
          borderTopRightRadius: '16px',
          borderBottomRightRadius: 0,
          px: 2,
          borderRightColor: '#919eab1f',
          borderTopColor: '#919eab1f',
          fontSize: '12px',
          minWidth: '100px',
        }}
        label="Rules"
        variant="outlined"
      />
    </Link>
  </Stack>
);

export default function AutomationLogFilters({
  setFilterObject,
  getSearchPlaceholder,
  searchQuery,
  setSearchQuery,
  sortValue,
  setSortValue,
  sort,
}) {
  const { allRuleList } = useSelector((state) => ({
    allRuleList: state?.automation?.allRuleTemplateList?.automationRuleTemplates,
  }));

  const dispatch = useDispatch();

  // Remove campaignSearch state
  const [ruleSearch, setRuleSearch] = useState('');

  useEffect(() => {
    // Removed campaign fetch effect
  }, [dispatch]);

  useEffect(() => {
    const credentials = {
      page: 1,
      limit: 10,
      search: ruleSearch,
      dispatch,
    };
    dispatch(getAllRuleTemplateList(credentials));
  }, [dispatch, ruleSearch]);

  const filteredAllRules = [
    ...(allRuleList || []).map((results) => ({
      name: results?.title,
      value: results?._id,
    })),
  ];

  const SORT_OPTIONS = [
    { value: -1, label: 'Latest' },
    { value: 1, label: 'Oldest' },
  ];

  const statusOptions = [
    { label: 'All', value: '', color: 'success' },
    { label: 'Pending', value: 'PENDING', color: 'success' },
    { label: 'Success', value: 'SUCCESS', color: 'success' },
    { label: 'Failed', value: 'FAILED', color: 'success' },
  ];

  const executionTypeOptions = [
    { label: 'All', value: '', color: 'success' },
    { label: 'Manual', value: 'MANUAL', color: 'success' },
    { label: 'Automatic', value: 'AUTOMATIC', color: 'success' },
  ];
  const campaignUpdateSchema = Yup.object().shape({
    automationRuleTemplate: Yup.array(),
    isActive: Yup.string(),
    executionType: Yup.string(),
  });

  const defaultValues = useMemo(
    () => ({
      automationRuleTemplate: [],
      isActive: '',
      executionType: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(campaignUpdateSchema),
    defaultValues,
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  console.log('errors', errors);

  const onSubmit = handleSubmit((filterFromData) => {
    // Clean the filter object: wrap arrays in {$in: ...}, convert isActive to boolean, remove empty strings
    const cleanedData = Object.keys(filterFromData).reduce((acc, key) => {
      const value = filterFromData[key];
      if (Array.isArray(value)) {
        if (value.length > 0) acc[key] = { $in: value };
      } else if (key === 'isActive') {
        if (value === 'true') acc[key] = true;
        else if (value === 'false') acc[key] = false;
      } else if (typeof value === 'string') {
        if (value !== '') acc[key] = value;
      } else if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
    setFilterObject(cleanedData);
  });

  // reset form
  const [resetKey, setResetKey] = useState(0);
  const handleResetFrom = () => {
    methods.reset(defaultValues);
    setFilterObject({});
    setResetKey((prevKey) => prevKey + 1);
  };

  const ToggleFilter = ({ label, name, options }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={watch(name) || ''}
        onChange={(event, newValue) => setValue(name, newValue)}
        exclusive
        size="small"
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            sx={{
              borderRadius: 2,
              px: 1.5,
              py: 0.3,
              '&.Mui-selected': {
                backgroundColor: `${option.color}.main`,
                color: 'white',
                '&:hover': {
                  backgroundColor: `${option.color}.dark`,
                },
              },
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
  ToggleFilter.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  };

  const ChipFilter = ({ label, name, options }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            size="small"
            clickable
            variant={watch(name) === option.value ? 'filled' : 'outlined'}
            color={watch(name) === option.value ? option.color : 'default'}
            onClick={() => {
              const current = watch(name);
              const newValue = current === option.value ? '' : option.value;
              setValue(name, newValue);
            }}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: `${option.color}.light`,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
  ChipFilter.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  };

  const FilterFooter = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ mt: 0, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Stack direction="row" spacing={1}>
        <Stack width="fit-content" py={0} px={3} direction="row" alignItems="center" spacing={0.6}>
          <FilterAltIcon fontSize="small" sx={{ fontSize: '10px' }} />
          <Typography
            variant="caption"
            color="text.disabled"
            textAlign="center"
            sx={{ fontSize: '10px' }}
          >
            {/* Once you modify any of these filter values, make sure to apply those filters by clicking
            the Apply Filters Button */}
            Adjust filters and click Apply Filters Button
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          sx={{ px: 2, borderRadius: 2 }}
          onClick={handleResetFrom}
        >
          Reset All
        </Button>
        <LoadingButton
          color="primary"
          type="submit"
          size="small"
          variant="contained"
          loading={isSubmitting}
          sx={{ borderRadius: 2, px: 2 }}
        >
          Apply Filters
        </LoadingButton>
      </Stack>
    </Stack>
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <CardContent sx={{ px: 0, pt: 1, mt: 0 }} className="remove-padding-bottom-card">
          {redirectLinks}
          <Stack spacing={3}>
            {/* Search + Sort */}
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <TextField
                className="search-field-normal"
                size="small"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 350, '& .MuiInputBase-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                value={sortValue}
                onChange={(e) => setSortValue(e.target.value)}
                label="Sort"
                sx={{ minWidth: 150, '& .MuiInputBase-root': { borderRadius: 2 } }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {sort !== AUTOMATION_SORT.RULE && (
                <Autocomplete
                  multiple
                  key={resetKey}
                  id="rule-select-demo"
                  options={filteredAllRules}
                  autoHighlight
                  size="small"
                  getOptionLabel={(option) => option.name}
                  onChange={(event, value) =>
                    setValue(
                      'automationRuleTemplate',
                      value.map((item) => item.value)
                    )
                  }
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Rule"
                      name="automationRuleTemplate"
                      className="autocomplete-custom"
                      placeholder="Select Rule"
                      onChange={(e) => setRuleSearch(e.target.value)}
                      inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                      sx={{
                        '& .MuiInputBase-root': {
                          borderRadius: 2,
                        },
                        minWidth: '200px',
                      }}
                    />
                  )}
                />
              )}
            </Stack>

            {/* Only show the rest of the filters and footer if not RULE */}
            {sort !== AUTOMATION_SORT.RULE && (
              <>
                {/* Filter Grid */}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ToggleFilter label="Status" name="status" options={statusOptions} />
                  <ToggleFilter
                    label="Execution Type"
                    name="executionType"
                    options={executionTypeOptions}
                  />
                </Stack>
                <FilterFooter />
              </>
            )}
          </Stack>
        </CardContent>
      </form>
    </FormProvider>
  );
}

AutomationLogFilters.propTypes = {
  sort: PropTypes.string,
  showHide: PropTypes.bool,
  setFilterObject: PropTypes.func,
  getSearchPlaceholder: PropTypes.func,
  searchQuery: PropTypes.string,
  setSearchQuery: PropTypes.func,
  sortValue: PropTypes.string,
  setSortValue: PropTypes.func,
};
